import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "@/components/ui/sonner";
import { I18nextProvider } from "react-i18next";
import { getI18nFor, migrateStoredLanguage } from "@/i18n";
import { getRequestLang } from "@/lib/lang.functions";
import { useContentFreshness } from "@/hooks/use-content-freshness";
import { getMaintenanceState } from "@/lib/maintenance.functions";
import { initAnalytics, trackPageView } from "@/lib/analytics";

const ALWAYS_ALLOWED = ["/admin", "/auth"];

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400..900&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";

function MaintenanceScreen({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="text-mono-caps text-amber mb-4">Mantenimiento</p>
        <h1 className="text-4xl">Youtubers Map</h1>
        <p className="mt-4 text-sm text-muted-foreground whitespace-pre-line">{message}</p>
      </div>
    </div>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="text-mono-caps text-amber mb-4">404 · Off the map</p>
        <h1 className="text-5xl">Uncharted territory</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Aquesta ruta no apareix al nostre atles.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block bg-ink text-paper px-5 py-2.5 text-sm font-medium hover:bg-amber transition-colors"
        >
          Torna a base
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: any; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="text-mono-caps text-amber mb-4">Error de connexió</p>
        <h1 className="text-3xl">Alguna cosa ha fallat</h1>
        <p className="mt-4 text-sm text-muted-foreground">{error.message}</p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="bg-ink text-paper px-4 py-2 text-sm font-medium hover:bg-amber transition-colors"
          >
            Reintenta
          </button>
          <a href="/" className="border border-ink/20 px-4 py-2 text-sm hover:bg-secondary transition-colors">
            Base
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Youtubers Map — Un atles dels youtubers viatgers" },
      {
        name: "description",
        content:
          "Agregador de youtubers viatgers. Explora els vídeos de Lethal Crysis, Clavero, Luisito Comunica, Zazza i molts més geolocalitzats en un mapa mundial.",
      },
      { name: "author", content: "Youtubers Map" },
      { property: "og:title", content: "Youtubers Map — Un atles dels youtubers viatgers" },
      {
        property: "og:description",
        content: "Mapa mundial dels vídeos dels millors youtubers viatgers, geolocalitzats.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png", sizes: "64x64" },
      { rel: "icon", type: "image/png", href: "/favicon-32.png", sizes: "32x32" },
      { rel: "apple-touch-icon", href: "/logo-icon.png" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      // Map tiles/style + data API: open the TCP+TLS handshake while JS is still parsing,
      // which removes a full round-trip from the critical request chain.
      { rel: "preconnect", href: "https://tiles.basemaps.cartocdn.com", crossOrigin: "" },
      { rel: "preconnect", href: import.meta.env.VITE_SUPABASE_URL, crossOrigin: "" },
    ],

  }),
  loader: async () => {
    const [maintenance, lang] = await Promise.all([getMaintenanceState(), getRequestLang()]);
    return { maintenance, lang };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const lang = Route.useLoaderData()?.lang ?? "es";
  return (
    <html lang={lang}>
      <head>
        <HeadContent />
        {/* Fonts loaded without blocking the initial render */}
        <link rel="preload" as="style" href={FONTS_HREF} />
        <noscript>
          <link rel="stylesheet" href={FONTS_HREF} />
        </noscript>
        <script
          dangerouslySetInnerHTML={{
            __html:
              `var l=document.createElement('link');l.rel='stylesheet';l.href=${JSON.stringify(FONTS_HREF)};document.head.appendChild(l);`,
          }}
        />
        <script
          defer
          src="https://umami.optispai.com/estadistiques.js"
          data-website-id="c55d3709-028b-4416-afcb-c438a1c7ace7"
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/** Invalidates cached data when the backoffice changes something. */
function FreshnessWatcher() {
  useContentFreshness();
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { maintenance, lang } = Route.useLoaderData();
  // Per-request language: a fixed clone on the server, the global instance on the client.
  const i18n = getI18nFor(lang);
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const blocked =
    maintenance.blocked && !ALWAYS_ALLOWED.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  useEffect(() => {
    migrateStoredLanguage();
    initAnalytics();
    const unsubscribe = router.history.subscribe(() => {
      trackPageView(window.location.pathname + window.location.search);
    });
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        router.invalidate();
        if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
      }
    });
    return () => {
      unsubscribe();
      data.subscription.unsubscribe();
    };
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <FreshnessWatcher />
        <main>{blocked ? <MaintenanceScreen message={maintenance.message} /> : <Outlet />}</main>
        <Toaster position="top-right" />
      </I18nextProvider>
    </QueryClientProvider>
  );
}
